import React, { useEffect, useState } from "react";
import { post } from "../helper/api_helper";
import Swal from 'sweetalert2';
import { useNavigate } from "react-router-dom";
import "./MenuPermission.css"; // Import the responsive CSS

const MenuPermission = () => {
  const navigate = useNavigate();
  const [tableData, setTableData] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [headerChecks, setHeaderChecks] = useState({
    list_permission: 0,
    add_permission: 0,
    edit_permission: 0,
    view_permission: 0,
    delete_permission: 0,
    print_permission: 0,
  });

  document.title = "Menu Permission | Ruth";

  const showAlert = (icon, title, text, timer = 2000) => {
    Swal.fire({ icon, title, text: text || 'Something went wrong.', timer, showConfirmButton: false });
  };

  useEffect(() => { fetchRoles(); }, []);
  useEffect(() => { if (selectedRoleId) fetchPermissions(); }, [selectedRoleId]);

  const fetchRoles = async () => {
    try {
      const response = await post("settings/role/list");
      setRoles(response.data);
    } catch (error) { console.error("Error fetching roles:", error); }
  };

  const fetchPermissions = async () => {
    try {
      const response = await post("settings/menu_permission/getPermission", { role_id_fk: selectedRoleId });
      const markCommon = (row) => ({
        ...row,
        common: row.list_permission || row.add_permission || row.edit_permission || row.view_permission || row.delete_permission || row.print_permission ? 1 : 0,
        child: row.child ? row.child.map(markCommon) : [],
        sub_child: row.sub_child ? row.sub_child.map(markCommon) : [],
      });
      const dataWithCommon = response.data.map(markCommon);
      setTableData(dataWithCommon);

      const newHeaderChecks = ["list_permission","add_permission","edit_permission","view_permission","delete_permission","print_permission"]
        .reduce((acc, key) => { acc[key] = dataWithCommon.every((row) => row[key] === 1 || row[key] === "1") ? 1 : 0; return acc; }, {});
      setHeaderChecks(newHeaderChecks);
    } catch (error) { console.error("Error fetching permissions:", error); }
  };

  const togglePermissionsRecursive = (rows, targetId) => rows.map(row => {
    if (row.menu_id === targetId) {
      const newVal = [row.list_permission,row.add_permission,row.edit_permission,row.view_permission,row.delete_permission,row.print_permission].every(v => v === 1 || v === "1") ? 0 : 1;
      return { ...row, list_permission: newVal, add_permission: newVal, edit_permission: newVal, view_permission: newVal, delete_permission: newVal, print_permission: newVal, common: newVal };
    }
    return { ...row, child: row.child ? togglePermissionsRecursive(row.child, targetId) : [], sub_child: row.sub_child ? togglePermissionsRecursive(row.sub_child, targetId) : [] };
  });

  const handleRowCheck = (id) => setTableData(togglePermissionsRecursive(tableData, id));

  const handleHeaderCheck = (column) => {
    const newVal = headerChecks[column] ? 0 : 1;
    setHeaderChecks(prev => ({ ...prev, [column]: newVal }));
    const updateColumnRecursive = (rows) => rows.map(row => ({ ...row, [column]: newVal, child: row.child ? updateColumnRecursive(row.child) : [], sub_child: row.sub_child ? updateColumnRecursive(row.sub_child) : [] }));
    setTableData(updateColumnRecursive(tableData));
  };

  const handleCellChange = (id, column) => {
    const updateRecursive = (rows) => rows.map(row => {
      if (row.menu_id === id) {
        const updatedRow = { ...row, [column]: row[column] ? 0 : 1 };
        updatedRow.common = [updatedRow.list_permission, updatedRow.add_permission, updatedRow.edit_permission, updatedRow.view_permission, updatedRow.delete_permission, updatedRow.print_permission].some(v => v === 1 || v === "1") ? 1 : 0;
        return updatedRow;
      }
      return { ...row, child: row.child ? updateRecursive(row.child) : [], sub_child: row.sub_child ? updateRecursive(row.sub_child) : [] };
    });
    const updated = updateRecursive(tableData);
    const allColumnChecked = updated.every(row => row[column] === 1 || row[column] === "1");
    setHeaderChecks(prev => ({ ...prev, [column]: allColumnChecked ? 1 : 0 }));
    setTableData(updated);
  };

  const handleRoleChange = (e) => setSelectedRoleId(e.target.value);

  const handleSubmit = async () => {
    const flattenRows = (rows) => rows.flatMap(row => [row, ...(row.child ? flattenRows(row.child) : []), ...(row.sub_child ? flattenRows(row.sub_child) : [])]);
    try {
      const flatRows = flattenRows(tableData);
      const payload = flatRows.filter(({list_permission,add_permission,edit_permission,view_permission,delete_permission,print_permission}) => list_permission||add_permission||edit_permission||view_permission||delete_permission||print_permission)
        .map(({menu_id,list_permission,add_permission,edit_permission,view_permission,delete_permission,print_permission}) => ({ role_id_fk: selectedRoleId, menu_id_fk: menu_id, list_permission, add_permission, edit_permission, view_permission, delete_permission, print_permission }));
      const response = await post("settings/menu_permission/savePermission", { role_id_fk: selectedRoleId, permission_list: payload });
      if (response.status === 200) { setSelectedRoleId(''); showAlert('success','Menu Permission',response.message); navigate(0); }
      else { showAlert('error',response.status,response.message); }
    } catch (error) { showAlert('error','Unexpected Error',error.message); }
  };

  const renderRows = (data, level=1, serial={count:1}) => data.map(row => {
    const isAllPermissionsChecked = [row.list_permission,row.add_permission,row.edit_permission,row.view_permission,row.delete_permission,row.print_permission].every(v => v===1||v==="1");
    const currentSerial = serial.count++;
    return (
      <React.Fragment key={row.menu_id}>
        <tr className={row.is_child===1?"parent_menu text-light":""}>
          <td data-label="S.No" style={{textAlign:"center"}}>{currentSerial}</td>
          <td data-label="Menu Name" style={{textAlign:"left","--level-indent": `${level * 10}px`}}><b>{row.menu_name}</b></td>
          <td data-label="Common" style={{textAlign:"center"}}><input type="checkbox" className="form-check-input border border-neutral-300" checked={isAllPermissionsChecked} onChange={()=>handleRowCheck(row.menu_id)}/></td>
          {["list_permission","add_permission","edit_permission","view_permission","delete_permission","print_permission"].map(col=>(
            <td key={col} data-label={col.replace("_permission","").toUpperCase()} style={{textAlign:"center"}}>
              <input type="checkbox" className="form-check-input border border-neutral-300" checked={row[col]===1||row[col]==="1"} onChange={()=>handleCellChange(row.menu_id,col)}/>
            </td>
          ))}
        </tr>
        {row.child&&renderRows(row.child,level+1,serial)}
        {row.sub_child&&renderRows(row.sub_child,level+2,serial)}
      </React.Fragment>
    );
  });

  return (
    <>
      <div className="page-header">
        <div>
          <div className="page-header-title">Menu Permission</div>
          <div className="page-header-sub">Manage Access Rights</div>
        </div>
      </div>
      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="card-body">
              <div className="row">
                <div className="col-md-3">
                  <label className="form-label fw-semibold text-primary-light text-sm mb-8">Role</label>
                  <select className="form-control radius-4 form-select" value={selectedRoleId} onChange={handleRoleChange}>
                    <option value="">Select</option>
                    {roles.map(role => <option key={role.role_id} value={role.role_id}>{role.role_name}</option>)}
                  </select>
                </div>
              </div>
            </div>
          </div>

          <br />
          {selectedRoleId && (
            <div className="card shadow-lg border border-secondary rounded-3 fw-bold">
              <div className="card-body">
                <table className="table table-bordered">
                  <thead className="bg-success text-light">
                    <tr>
                      <th data-label="S.No" style={{textAlign:"center"}}>S.No</th>
                      <th data-label="Menu Name" style={{textAlign:"left"}}>Menu Name</th>
                      <th data-label="Common" style={{textAlign:"center"}}>Common</th>
                      {["list_permission","add_permission","edit_permission","view_permission","delete_permission","print_permission"].map(col=>(
                        <th key={col} data-label={col.replace("_permission","").toUpperCase()} style={{textAlign:"center"}}>
                          {col.replace("_permission","").toUpperCase()}<br/>
                          <input className="form-check-input border border-neutral-300" type="checkbox" checked={headerChecks[col]===1} onChange={()=>handleHeaderCheck(col)}/>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>{renderRows(tableData,1,{count:1})}</tbody>
                </table>
              </div>
              <div className="card-footer">
                <div className="row g-3">
                  <div className="col-md-12">
                    <button type="submit" onClick={handleSubmit} className="btn btn-sm btn-primary pull-right text-sm px-24 py-4 radius-4 fw-medium" style={{float:"right"}}>SAVE</button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default MenuPermission;